import React, { useState, useRef, useEffect, useMemo } from "react";
import { X, Upload, ChevronRight, CheckCircle2, Sparkles, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import DataCheckPanel from "../../../components/import/DataCheckPanel";
import ListKindPicker from "../../../components/import/ListKindPicker";
import {
  previewImport,
  commitImport,
  previewWorkbook,
  commitWorkbook,
  checkImport,
} from "../../../api/database";
import { importError } from "../importError";
import { PIPELINE_PLACEMENTS, PIPELINE_HINTS } from "./MoveToPipelineModal";

/**
 * Import flow with two paths:
 *  - .xlsx  -> workbook mode: server auto-cleans every tab (messy real sheets),
 *             shows a per-sheet review with row accounting, then commits.
 *  - .csv   -> classic 3-step manual column mapping for a single clean file.
 */
const NEW_GROUP = "__new_group__";

// A column is judged by its cells, not its heading. Deliberately blunt: only a
// clear majority of one shape counts, so a column of mixed notes stays "text".
const EMAIL_CELL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const digitsOf = (v) => v.replace(/[^0-9]/g, "");

function contentKind(values) {
  if (!values.length) return "empty";
  const share = (test) => values.filter(test).length / values.length;
  if (share((v) => EMAIL_CELL.test(v)) >= 0.6) return "email";
  if (share((v) => {
    const d = digitsOf(v);
    return d.length >= 8 && d.length <= 13 && d.length >= 0.6 * v.replace(/\s/g, "").length;
  }) >= 0.6) return "phone";
  return "text";
}

// The field keys that expect one exact shape, and the ones a contact detail
// must never be filed under.
const FIELD_WANTS = {
  email: "email",
  second_email: "email",
  mobile: "phone",
  second_mobile: "phone",
};
const CONTACT_FIELDS = new Set(Object.keys(FIELD_WANTS));
const KIND_LABEL = { email: "email addresses", phone: "phone numbers", text: "text" };

// "Block Owners - Block Leads", so two same-named lists in different categories
// can be told apart. A heading used as a list of its own shows as just the heading.
const listLabel = (l) =>
  l.group_name && l.group_name.trim().toLowerCase() !== (l.name || "").trim().toLowerCase()
    ? `${l.group_name} - ${l.name}`
    : l.name;

// The Database list a Prospects import is kept under when the agent doesn't
// pick one. Re-imports adopt it by name, so it stays one tab.
export const PIPELINE_IMPORT_LIST = "Pipeline imports";

// Which Prospects column the imported owners go into. Christian Gravias,
// Monday 13423268504 (30 Oct 2026): "When they import there prospects please
// have a section where agents can choose which pipeline to move the property
// into". Same columns as Database > Move to Pipeline.
function PipelinePlacementPicker({ value, onChange, workbook }) {
  return (
    <div className="mb-4 rounded-lg border border-[#55c5d0] bg-[#f4fbfc] p-3">
      <div className="text-xs font-extrabold uppercase tracking-wide text-gray-500 mb-2" id="import-pipeline-placement">
        Which pipeline should they go into?
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" role="radiogroup" aria-labelledby="import-pipeline-placement">
        {PIPELINE_PLACEMENTS.map((p) => {
          const on = value === p.value;
          return (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(p.value)}
              className={`py-2 rounded-lg border text-sm font-semibold transition-colors ${
                on ? p.on : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-gray-500">{PIPELINE_HINTS[value]}</p>
      <p className="text-[11px] text-gray-400">
        Every row is kept in your Database too{workbook ? ". Buyer tabs stay in the Database only." : "."}
      </p>
    </div>
  );
}

export default function ImportWizard({ view, lists, groups = [], activeList = "", pipeline = false, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [placement, setPlacement] = useState("new");

  // CSV mode
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  // Server data check for the current mapping (see DataCheckPanel), and what the
  // agent says this list is. Both must be settled before the import saves.
  const [quality, setQuality] = useState(null);
  const [checking, setChecking] = useState(false);
  const [listKind, setListKind] = useState("");
  const [wbKinds, setWbKinds] = useState({ owners: "", buyers: "" });
  const [wbListKinds, setWbListKinds] = useState({});

  // Re-check the data whenever the agent changes the column mapping.
  useEffect(() => {
    if (step !== 2 || !preview) return undefined;
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await checkImport({ view, mapping, rows: preview.rows || preview.sample });
        setQuality(res.data);
      } catch (e) {
        // Keep the last result; the commit re-checks on the server anyway.
      } finally {
        setChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [step, preview, mapping, view]);
  const [listChoice, setListChoice] = useState(activeList ? String(activeList) : "");
  const [newListName, setNewListName] = useState(pipeline ? PIPELINE_IMPORT_LIST : "");
  // The category a NEW list is filed under. "" = none (a top-level list),
  // NEW_GROUP = one being typed. Christian Gravias / Simon Abbott, Monday
  // 12908273441 (2026-08-27): a CSV could only ever make a list of its own, so
  // filing one under a category that already existed was impossible - "I need
  // to be able to create a Sheet inside of a Group and upload a CSV to the
  // sheet without creating a new group". Caleb Pikoulas reported the same gap
  // as CRM-2026-00010.
  const [groupChoice, setGroupChoice] = useState("");
  const [newGroupName, setNewGroupName] = useState("");

  // Workbook mode
  const [wbSheets, setWbSheets] = useState(null);
  const [wbResult, setWbResult] = useState(null);

  // Drag the dialog around by its title bar (so the grid behind stays readable)
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const dragRef = useRef(null);

  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      baseLeft: rect.left - pos.x,
      baseTop: rect.top - pos.y,
      w: rect.width,
    };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
      // Keep a strip of the dialog on screen so it can always be dragged back
      setPos({
        x: clamp(
          d.originX + e.clientX - d.startX,
          80 - d.w - d.baseLeft,
          window.innerWidth - 80 - d.baseLeft
        ),
        y: clamp(
          d.originY + e.clientY - d.startY,
          -d.baseTop,
          window.innerHeight - 60 - d.baseTop
        ),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Drop a file straight onto the picker
  const [dropActive, setDropActive] = useState(false);

  const acceptFile = (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      toast.error("Only .csv or .xlsx files can be imported");
      return;
    }
    setFile(f);
  };

  const isWorkbook = (file?.name || "").toLowerCase().endsWith(".xlsx");

  const onNextFromFile = async () => {
    if (!file) return;
    setBusy(true);
    try {
      if (isWorkbook) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await previewWorkbook(fd);
        setWbListKinds(res.data.list_kinds || {});
        // A one-tab workbook uploaded while a list is open goes into that list
        // by default, the same as a CSV does. Still changeable in the review.
        const open = (lists || []).find((l) => String(l.id) === String(activeList));
        const single = res.data.sheets.length === 1;
        setWbSheets(
          res.data.sheets.map((s) => ({
            ...s,
            include: true,
            group_name: s.group_name || "",
            // Grouping is keyed on the suburb DETECTED at preview time, not on
            // the editable title — otherwise retyping the heading would
            // regroup the table (and steal focus) on every keystroke.
            group_key: s.group_name || "_ungrouped",
            // "" = this tab makes (or reuses by name) a list of its own.
            list_id: single && open && (open.view || view) === s.view ? String(open.id) : "",
          }))
        );
        setStep("wb-review");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("view", view);
        const res = await previewImport(fd);
        setPreview(res.data);
        setMapping(res.data.suggested_mapping || {});
        setQuality(res.data.quality || null);
        setStep(2);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || importError(e));
    } finally {
      setBusy(false);
    }
  };

  const doCsvCommit = async () => {
    setBusy(true);
    try {
      const res = await commitImport({
        view,
        filename: preview.filename,
        mapping,
        custom_columns: [],
        rows: preview.rows || preview.sample,
        list_kind: listKind,
        upload_token: preview.upload_token || "",
        list_id: listChoice || null,
        // A category with no list name typed under it is a destination in its
        // own right: the category IS the tab. The backend falls back to the
        // same thing, so the two agree.
        new_list_name: newListName.trim() || chosenGroup || null,
        // Only meaningful alongside new_list_name — an existing list keeps the
        // category it is already under.
        group_name: listChoice ? null : chosenGroup || null,
        ...(pipeline ? { pipeline_placement: placement } : {}),
      });
      toast.success(res.data.detail || "Imported");
      onDone && onDone();
      onClose();
    } catch (e) {
      // The server re-runs the data check; if it still blocks, show why.
      if (e?.response?.data?.quality) {
        setQuality(e.response.data.quality);
        setStep(2);
      }
      toast.error(importError(e));
    } finally {
      setBusy(false);
    }
  };

  const doWorkbookCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append(
        "sheets",
        JSON.stringify(
          wbSheets.map((s) => ({
            sheet: s.sheet,
            view: s.view,
            list_name: s.list_name,
            group_name: (s.group_name || "").trim(),
            include: s.include,
            list_id: s.list_id || null,
          }))
        )
      );
      Object.entries(wbKinds).forEach(([v, k]) => k && fd.append(`list_kind_${v}`, k));
      if (pipeline) fd.append("pipeline_placement", placement);
      const res = await commitWorkbook(fd);
      setWbResult(res.data);
      setStep("wb-done");
      onDone && onDone();
    } catch (e) {
      toast.error(importError(e));
    } finally {
      setBusy(false);
    }
  };

  // Every category that already exists on this Database, so an import can be
  // filed under one instead of only ever inventing a new one. Built from the
  // page's categories plus whatever the lists themselves carry, so a category
  // is offered even when the caller didn't pass it.
  const knownGroups = useMemo(() => {
    const seen = new Map();
    [...(groups || []), ...(lists || []).map((l) => l.group_name)].forEach((raw) => {
      const title = (raw || "").trim();
      if (title && !seen.has(title.toLowerCase())) seen.set(title.toLowerCase(), title);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [groups, lists]);

  // The category a new CSV list will sit under: either one that already exists
  // or the one being typed.
  const chosenGroup = (groupChoice === NEW_GROUP ? newGroupName : groupChoice).trim();

  // Where the rows will land. A category on its own IS a destination — it
  // becomes the tab — so choosing one and leaving the list name blank is a
  // complete answer, not a half-finished form. Without any of the three the
  // rows are saved under no tab at all and the agent cannot find them again
  // (Paul Jimoulis, CRM-2026-00017).
  const destinationList = listChoice
    ? (lists.find((l) => String(l.id) === String(listChoice))?.name || "the list")
    : newListName.trim() || chosenGroup;
  const hasDestination = Boolean(destinationList);

  // Fields that more than one column claims. The server writes them in header
  // order, so the last column silently wins and the other one's data is thrown
  // away — worth saying out loud before the commit rather than after.
  const duplicateFields = useMemo(() => {
    const seen = new Map();
    Object.values(mapping || {}).forEach((key) => {
      if (key) seen.set(key, (seen.get(key) || 0) + 1);
    });
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [mapping]);

  const duplicateFieldLabels = useMemo(() => {
    const byKey = new Map(
      ((preview && preview.standard_fields) || []).map((f) => [f.key, f.label])
    );
    return [...duplicateFields].map((k) => byKey.get(k) || k);
  }, [duplicateFields, preview]);

  // What each column actually CONTAINS, so the agent can see what they are
  // mapping instead of trusting the heading. A sheet exported with a stray
  // empty column pushes the list one row out of step, and the mapping came
  // back with the EMAIL column pointed at the Note: every row's address was
  // written into the Last Note and the note the agent meant to input never
  // arrived (Paul Jimoulis, CRM-2026-00020).
  const columnContent = useMemo(() => {
    const out = {};
    ((preview && preview.headers) || []).forEach((h) => {
      const values = ((preview && preview.sample) || [])
        .map((r) => String(r[h] ?? "").trim())
        .filter(Boolean);
      out[h] = { values, kind: contentKind(values) };
    });
    return out;
  }, [preview]);

  // Columns whose contents cannot be what the chosen field is. Only the
  // unmistakable cases: a column of email addresses or phone numbers pointed
  // somewhere that is neither.
  const misfits = useMemo(() => {
    const byKey = new Map(
      ((preview && preview.standard_fields) || []).map((f) => [f.key, f.label])
    );
    const out = {};
    ((preview && preview.headers) || []).forEach((h) => {
      const field = mapping[h];
      const kind = (columnContent[h] || {}).kind;
      if (!field || !kind || kind === "empty" || kind === "text") return;
      const wants = FIELD_WANTS[field];
      if (wants === kind) return;
      if (!wants && !CONTACT_FIELDS.has(field)) {
        out[h] = `holds ${KIND_LABEL[kind]} but is mapped to “${byKey.get(field) || field}”`;
      } else if (wants && wants !== kind) {
        out[h] = `holds ${KIND_LABEL[kind]}, not ${KIND_LABEL[wants]}`;
      }
    });
    return out;
  }, [columnContent, mapping, preview]);

  const updateSheet = (i, patch) =>
    setWbSheets((rows) => rows.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  // Retitle a whole group at once: the suburb is one heading over all of its
  // tabs, so it is edited in one place rather than on every row.
  const updateGroup = (key, patch) =>
    setWbSheets((rows) => rows.map((s) => (s.group_key === key ? { ...s, ...patch } : s)));

  // Tabs grouped under their suburb heading — "Preston" over "1 bedroom unit",
  // "2 bedroom unit", "3 bedroom unit"… (Christian Gravias, 2026-08-06).
  const wbGroups = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    (wbSheets || []).forEach((s, i) => {
      const key = s.group_key || "_ungrouped";
      if (!byKey.has(key)) {
        const g = { key, title: s.group_name || "", rows: [] };
        byKey.set(key, g);
        groups.push(g);
      }
      byKey.get(key).rows.push({ ...s, index: i });
    });
    // "1 bedroom unit" before "2 bedroom unit" before "10 bedroom unit" — a
    // plain sort would put 10 second.
    const lead = (t) => {
      const m = /\d+/.exec(t || "");
      return m ? Number(m[0]) : Number.MAX_SAFE_INTEGER;
    };
    groups.forEach((g) => {
      g.rows.sort(
        (a, b) =>
          lead(a.list_name) - lead(b.list_name) ||
          (a.list_name || "").localeCompare(b.list_name || "")
      );
      g.records = g.rows.reduce((n, s) => n + (s.include ? s.record_count : 0), 0);
      g.allOn = g.rows.every((s) => s.include);
    });
    // Named suburbs first, loose tabs last.
    return groups.sort(
      (a, b) =>
        (a.key === "_ungrouped" ? 1 : 0) - (b.key === "_ungrouped" ? 1 : 0) ||
        a.key.localeCompare(b.key)
    );
  }, [wbSheets]);

  const groupCount = wbGroups.filter(
    (g) => g.title.trim() && g.rows.some((s) => s.include)
  ).length;

  // The result screen, grouped the same way as the review screen.
  const doneGroups = useMemo(() => {
    const groups = [];
    const byTitle = new Map();
    ((wbResult && wbResult.sheets) || []).forEach((s) => {
      const title = s.group || "";
      if (!byTitle.has(title)) {
        const g = { title, rows: [] };
        byTitle.set(title, g);
        groups.push(g);
      }
      byTitle.get(title).rows.push(s);
    });
    return groups.sort((a, b) => (a.title ? 0 : 1) - (b.title ? 0 : 1) || a.title.localeCompare(b.title));
  }, [wbResult]);

  const totalToImport = (wbSheets || [])
    .filter((s) => s.include)
    .reduce((n, s) => n + s.record_count, 0);

  // Per-tab data checks and the list types still to be answered.
  const wbIncluded = (wbSheets || []).filter((s) => s.include);
  const wbChecks = wbIncluded.filter((s) => s.quality?.issues?.length);
  const wbBlocked = wbIncluded.some((s) => s.quality?.blocking);
  const wbViews = ["owners", "buyers"].filter((v) => wbIncluded.some((s) => s.view === v));

  // Dates the server had to read day-first because the sheet was saved month-first.
  const datesCorrected = (wbSheets || [])
    .filter((s) => s.include)
    .reduce((n, s) => n + (s.dates_corrected || 0), 0);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <div
        ref={panelRef}
        style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
        className="bg-white rounded-xl shadow-2xl w-[720px] max-w-full max-h-[88vh] flex flex-col"
      >
        <div
          onMouseDown={startDrag}
          title="Drag to move"
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200 cursor-move select-none"
        >
          <h3 className="font-bold text-gray-900">
            {step === "wb-review" || step === "wb-done"
              ? pipeline
                ? "Import prospects to your Pipeline — auto-cleaned"
                : "Import spreadsheet — auto-cleaned"
              : pipeline
              ? `Import prospects to your Pipeline — Step ${step} of 3`
              : `Import ${view === "owners" ? "Owners" : "Buyers"} CSV — Step ${step} of 3`}
          </h3>
          <button onClick={onClose} className="text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-auto">
          {/* STEP 1 — choose file */}
          {step === 1 && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Upload a spreadsheet. A messy multi-tab <b>.xlsx</b> (every tab a list) is
                <b> cleaned automatically</b> — no column mapping needed. A single <b>.csv</b> goes
                through manual mapping.
              </p>
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropActive(true);
                }}
                onDragLeave={() => setDropActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropActive(false);
                  acceptFile(e.dataTransfer?.files?.[0]);
                }}
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-2 cursor-pointer ${
                  dropActive
                    ? "border-[#55c5d0] bg-[#eaf7f8]"
                    : "border-gray-300 hover:border-[#55c5d0]"
                }`}
              >
                <Upload size={26} className="text-[#55c5d0]" />
                <span className="text-sm font-semibold text-gray-700">
                  {file
                    ? file.name
                    : dropActive
                    ? "Drop the file to upload"
                    : "Drag a .xlsx or .csv file here, or click to choose"}
                </span>
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              {isWorkbook && (
                <div className="mt-3 text-xs text-[#0e7d86] bg-[#eaf7f8] border border-[#bfe7ea] rounded-lg p-3 flex items-center gap-2">
                  <Sparkles size={14} /> Multi-tab workbook detected — it'll be auto-cleaned and split into lists.
                </div>
              )}
            </div>
          )}

          {/* WORKBOOK REVIEW */}
          {step === "wb-review" && wbSheets && (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Each tab was cleaned into a list, grouped under its <b>suburb</b>. Check the group
                title, the type and the list name, then import. The counts reconcile so{" "}
                <b>no record is dropped</b> (blank rows and section headings are skipped).
              </p>
              {pipeline && <PipelinePlacementPicker value={placement} onChange={setPlacement} workbook />}
              {wbChecks.length > 0 && (
                <div className="mb-3 space-y-2">
                  {wbChecks.map((s) => (
                    <div key={s.sheet}>
                      <div className="text-xs font-bold text-gray-700 mb-1">Tab “{s.sheet}”</div>
                      <DataCheckPanel quality={s.quality} compact />
                    </div>
                  ))}
                </div>
              )}
              {wbViews.length > 0 && (
                <div className="mb-3 grid gap-2">
                  {wbViews.map((v) => (
                    <ListKindPicker
                      key={v} name={`list-kind-${v}`}
                      label={v === "owners" ? "What are the owner tabs?" : "What are the buyer tabs?"}
                      options={wbListKinds[v] || []}
                      value={wbKinds[v]}
                      onChange={(k) => setWbKinds({ ...wbKinds, [v]: k })}
                    />
                  ))}
                </div>
              )}
              {/* One heading for the whole workbook. Christian Gravias
                  (2026-08-27): "Cant pick just 1 heading in Database" — the
                  detected suburbs could only be retyped one group at a time,
                  and there was no way to reach a category that already existed. */}
              <div className="mb-3 flex items-center gap-2 flex-wrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <MapPin size={13} className="text-[#0e7d86]" />
                <span className="text-xs font-semibold text-gray-600">
                  Put every tab under one category:
                </span>
                <select
                  value=""
                  onChange={(e) => {
                    const title = e.target.value;
                    if (!title) return;
                    // group_key is what the review table groups by. Retyping a
                    // title deliberately leaves it alone (regrouping mid-keystroke
                    // steals focus), but this is one explicit choice, so the rows
                    // are merged into a single visible group as well.
                    setWbSheets((rows) =>
                      rows.map((row) => ({
                        ...row,
                        group_name: title === "__none__" ? "" : title,
                        group_key: title === "__none__" ? "_ungrouped" : title,
                      }))
                    );
                  }}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">Keep the suburbs read from the tabs</option>
                  {knownGroups.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                  <option value="__none__">No category at all</option>
                </select>
                <span className="text-[11px] text-gray-400">
                  or type one per group below
                </span>
              </div>
              <div className="overflow-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2">Use</th>
                      <th className="text-left px-3 py-2">Tab → List</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Records</th>
                      <th className="text-left px-3 py-2">Row check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wbGroups.map((g) => (
                      <React.Fragment key={g.key}>
                        {/* Group title = the suburb. Its tabs sit under it as
                            sub-headings. Editing it retitles the whole group. */}
                        <tr className="border-t border-gray-200 bg-[#f4fbfc]">
                          <td className="px-3 py-2 align-middle">
                            <input
                              type="checkbox"
                              checked={g.allOn}
                              title={g.allOn ? "Skip every tab in this group" : "Use every tab in this group"}
                              onChange={(e) => updateGroup(g.key, { include: e.target.checked })}
                            />
                          </td>
                          <td colSpan={4} className="px-3 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <MapPin size={13} className="text-[#0e7d86]" />
                              <input
                                value={g.title}
                                list="import-known-groups"
                                onChange={(e) => updateGroup(g.key, { group_name: e.target.value })}
                                placeholder="Group title (e.g. Preston)"
                                title="The category these lists sit under — pick one you already have, or type a new one"
                                className="border border-[#bfe7ea] bg-white rounded px-2 py-1 text-sm font-bold text-gray-900 w-52"
                              />
                              <span className="text-[11px] text-gray-500">
                                {g.rows.length} list{g.rows.length === 1 ? "" : "s"} ·{" "}
                                {g.records.toLocaleString()} record{g.records === 1 ? "" : "s"}
                              </span>
                              {!g.title.trim() && (
                                <span className="text-[11px] text-gray-400">
                                  no suburb read from these tabs — type one to group them
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {g.rows.map((s) => (
                          <tr key={s.sheet} className="border-t border-gray-100">
                            <td className="px-3 py-2 pl-6">
                              <input
                                type="checkbox"
                                checked={s.include}
                                onChange={(e) => updateSheet(s.index, { include: e.target.checked })}
                              />
                            </td>
                            <td className="px-3 py-2 pl-6">
                              <div className="text-[11px] text-gray-400">{s.sheet}</div>
                              {/* Into a list that already exists, or a new one.
                                  Without this choice a workbook could only ever
                                  make lists of its own (Simon Abbott, Monday
                                  13202407781). */}
                              <select
                                value={s.list_id || ""}
                                aria-label={`Where the ${s.sheet} tab goes`}
                                onChange={(e) => updateSheet(s.index, { list_id: e.target.value })}
                                className={`border rounded px-2 py-1 text-sm w-56 mb-1 bg-white ${
                                  s.list_id ? "border-[#55c5d0]" : "border-gray-200"
                                }`}
                              >
                                <option value="">New list (name below)</option>
                                {(lists || [])
                                  .filter((l) => (l.view || view) === s.view)
                                  .map((l) => (
                                    <option key={l.id} value={l.id}>
                                      Add to: {listLabel(l)}
                                    </option>
                                  ))}
                              </select>
                              {!s.list_id && (
                                <input
                                  value={s.list_name}
                                  aria-label={`New list name for ${s.sheet}`}
                                  onChange={(e) => updateSheet(s.index, { list_name: e.target.value })}
                                  className="block border border-gray-200 rounded px-2 py-1 text-sm w-56"
                                />
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={s.view}
                                // An existing list already has a type: owner rows
                                // cannot go into a buyers list.
                                disabled={Boolean(s.list_id)}
                                onChange={(e) => updateSheet(s.index, { view: e.target.value, list_id: "" })}
                                className="border border-gray-200 rounded px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                              >
                                <option value="owners">Owners</option>
                                <option value="buyers">Buyers</option>
                              </select>
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-gray-900">{s.record_count}</td>
                            <td className="px-3 py-2 text-[11px] text-gray-500">
                              {s.stats.total} rows = {s.record_count} records + {s.stats.blank} blank +{" "}
                              {s.stats.header + s.stats.divider} headings
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-sm font-bold text-gray-800">
                {totalToImport.toLocaleString()} records will be imported across{" "}
                {wbSheets.filter((s) => s.include).length} lists
                {groupCount > 0 && ` in ${groupCount} group${groupCount === 1 ? "" : "s"}`}.
              </div>
              {/* The categories this Database already has, offered on every group
                  title box so an existing one is one click rather than retyped
                  (and misspelled into a second group). */}
              <datalist id="import-known-groups">
                {knownGroups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
              {datesCorrected > 0 && (
                <div className="mt-3 text-xs text-[#8a5a00] bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <b>{datesCorrected} date{datesCorrected === 1 ? "" : "s"} read day-first.</b>{" "}
                  This spreadsheet is set to a US locale, so dates like 12/08/2004 were
                  stored as 8 December instead of 12 August. They have been corrected to
                  the Australian reading. Set the sheet's locale to Australia to stop it
                  happening again.
                </div>
              )}
            </div>
          )}

          {/* WORKBOOK DONE */}
          {step === "wb-done" && wbResult && (
            <div className="text-center py-6">
              <CheckCircle2 size={40} className="text-[#34b56a] mx-auto mb-3" />
              <div className="text-lg font-bold text-gray-900">{wbResult.detail}</div>
              {/* Same shape as the review screen: suburb heading, its lists under it. */}
              <div className="mt-4 text-left max-w-sm mx-auto space-y-3">
                {doneGroups.map((g) => (
                  <div key={g.title}>
                    {g.title && (
                      <div className="text-[11px] font-extrabold uppercase tracking-wide text-[#0e7d86] mb-1">
                        {g.title}
                      </div>
                    )}
                    <div className="space-y-1">
                      {g.rows.map((s) => (
                        <div
                          key={`${g.title}-${s.list}`}
                          className={`flex justify-between text-sm text-gray-600 ${g.title ? "pl-3" : ""}`}
                        >
                          <span>{s.list}</span>
                          {/* Rows already in the Database are filed under this
                              list too, so they count: "+0" over a list that just
                              gained rows reads as a failed import. */}
                          <b className="text-gray-900">+{s.total ?? s.new}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* A cell the sheet held that the column couldn't. Shown rather
                  than changed quietly, so the agent can correct the source. */}
              {wbResult.trimmed?.length > 0 && (
                <div className="mt-5 text-left max-w-sm mx-auto rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <div className="text-[11px] font-extrabold uppercase tracking-wide text-amber-700 mb-1">
                    Tidied on the way in
                  </div>
                  <ul className="text-xs text-amber-900 space-y-0.5">
                    {wbResult.trimmed.slice(0, 6).map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* CSV STEP 2 — mapping */}
          {step === 2 && preview && (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Map each column ({preview.row_count} rows) to a field. Leave as “— skip —” to ignore.
              </p>
              <div className="space-y-2 max-h-[42vh] overflow-auto">
                {preview.headers.map((h, i) => (
                  // Keyed on the position, not the text: a spreadsheet exported
                  // with two unnamed columns gives two identical "" headers and
                  // React would collapse them onto one row.
                  <div key={`${i}-${h}`} className="flex items-center gap-3">
                    <span className="w-1/2 min-w-0 text-sm">
                      {/* The heading alone is not enough to map a column by: an
                          unnamed one read as a blank gap and the dropdown beside
                          it looked like it belonged to the row above, which is
                          how a mapping came back with Email→Note (Paul Jimoulis,
                          CRM-2026-00020). Show the column's own cells under it,
                          so what is being mapped is never in doubt. */}
                      <span className="block font-semibold text-gray-800 truncate">
                        {/* The server names an unnamed column after its
                            position now; this fallback covers a preview taken
                            before it did. */}
                        {h ? h : <span className="text-gray-400 italic">Column {i + 1} (no heading)</span>}
                      </span>
                      <span className="block text-xs text-gray-400 truncate">
                        {(columnContent[h]?.values || []).length
                          ? (columnContent[h].values.slice(0, 3).join(", "))
                          : "(empty in the first rows)"}
                      </span>
                    </span>
                    <ChevronRight size={14} className="text-gray-300 shrink-0" />
                    <select
                      value={mapping[h] || ""}
                      onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                      className={`w-1/2 border rounded-lg px-2 py-1.5 text-sm ${
                        duplicateFields.has(mapping[h]) || misfits[h]
                          ? "border-amber-400 bg-amber-50"
                          : "border-gray-200"
                      }`}
                    >
                      <option value="">— skip —</option>
                      {preview.standard_fields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {/* A column whose cells cannot be what the chosen field is. The
                  server files such a cell by its content rather than by the
                  column it was mapped to, but say so here so the mapping can be
                  corrected before the commit. */}
              {Object.keys(misfits).length > 0 && (
                <div className="mt-3 text-xs text-[#8a5a00] bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <b>Check these columns:</b>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {Object.entries(misfits).map(([h, why]) => (
                      <li key={h}>
                        <b>{h}</b> {why}.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Two columns pointing at one field means the later one silently
                  wins on the server and the other column's data is lost. */}
              {duplicateFields.size > 0 && (
                <div className="mt-3 text-xs text-[#8a5a00] bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <b>
                    {duplicateFields.size === 1
                      ? "Two columns are mapped to the same field"
                      : `${duplicateFields.size} fields have two columns mapped to them`}
                    :
                  </b>{" "}
                  {duplicateFieldLabels.join(", ")}. Only one of them will be kept — set the
                  others to “— skip —” or point them somewhere else.
                </div>
              )}
              {/* The server's check of EVERY row, not just the first few. */}
              <div className="mt-3">
                {checking && !quality ? (
                  <div className="text-xs text-gray-500">Checking the data…</div>
                ) : (
                  <DataCheckPanel quality={quality} />
                )}
              </div>
            </div>
          )}

          {/* CSV STEP 3 — where the rows land */}
          {step === 3 && (
            <div>
              {pipeline && <PipelinePlacementPicker value={placement} onChange={setPlacement} />}
              <div className="mb-4">
                <ListKindPicker options={preview?.list_kinds || []} value={listKind} onChange={setListKind} />
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {pipeline ? "Which Database list should they be kept under?" : "Where should these rows go?"}
              </p>

              {/* A — an existing list (including a heading being used as one) */}
              <div
                className={`rounded-lg border p-3 mb-3 ${
                  listChoice ? "border-[#55c5d0] bg-[#f4fbfc]" : "border-gray-200"
                }`}
              >
                <div className="text-xs font-extrabold uppercase tracking-wide text-gray-500 mb-2">
                  Add to a list that already exists
                </div>
                <select
                  value={listChoice}
                  onChange={(e) => {
                    setListChoice(e.target.value);
                    if (e.target.value) setNewListName("");
                  }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">— Choose a list —</option>
                  {/* Grouped lists show their heading, so two "2 Bedroom Unit"
                      lists in different suburbs can be told apart. A heading
                      used as a list of its own shows as just the heading. */}
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {listLabel(l)}
                    </option>
                  ))}
                </select>
              </div>

              {/* B — a new list, optionally filed under a heading that already
                  exists. Simon Abbott (2026-08-27): "I need to be able to create
                  a Sheet inside of a Group and upload a CSV to the sheet without
                  creating a new group." Caleb Pikoulas asked for the same thing
                  as CRM-2026-00010. */}
              <div
                className={`rounded-lg border p-3 ${
                  newListName.trim() ? "border-[#55c5d0] bg-[#f4fbfc]" : "border-gray-200"
                }`}
              >
                <div className="text-xs font-extrabold uppercase tracking-wide text-gray-500 mb-2">
                  …or start a new list
                </div>
                <input
                  value={newListName}
                  onChange={(e) => {
                    setNewListName(e.target.value);
                    if (e.target.value) setListChoice("");
                  }}
                  placeholder="New list name (e.g. Expired Listings)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                />
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <MapPin size={13} className="text-[#0e7d86]" />
                  <span className="text-xs font-semibold text-gray-600">Inside a category</span>
                  <select
                    value={groupChoice}
                    onChange={(e) => {
                      setGroupChoice(e.target.value);
                      if (e.target.value !== NEW_GROUP) setNewGroupName("");
                    }}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="">No category (its own tab)</option>
                    {knownGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                    <option value={NEW_GROUP}>+ New category…</option>
                  </select>
                  {groupChoice === NEW_GROUP && (
                    <input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="New category name, e.g. Rentals"
                      autoFocus
                      className="border border-[#bfe7ea] rounded-lg px-2 py-1.5 text-sm w-44 bg-white"
                    />
                  )}
                </div>
                {chosenGroup && newListName.trim() && (
                  <div className="mt-2 text-[11px] text-[#0e7d86]">
                    Lands as <b>{chosenGroup}</b> › <b>{newListName.trim()}</b>
                    {knownGroups.some((g) => g.toLowerCase() === chosenGroup.toLowerCase())
                      ? " — inside the category you already have, no new group."
                      : " — a new category."}
                  </div>
                )}
                {chosenGroup && !newListName.trim() && (
                  <div className="mt-2 text-[11px] text-[#0e7d86]">
                    Lands in <b>{chosenGroup}</b> — the category itself is the list. Add a
                    name above only if you want a tab inside it.
                  </div>
                )}
              </div>

              {!hasDestination && (
                <div className="mt-4 text-xs text-[#8a5a00] bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <b>Pick a list or a category first.</b> Rows imported without one are
                  saved but don't appear under any of your tabs, so there is no way to
                  work them.
                </div>
              )}

              <div className="mt-4 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
                On commit, records are de-duplicated ({view === "owners" ? "by address" : "by mobile then email"}).
                Existing records are kept; only empty fields are filled — nothing is overwritten.
                {hasDestination && (
                  <>
                    {" "}
                    Anyone already in your Database is <b>added to {destinationList}</b> as
                    well, and stays in the list they were already in.
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button
            onClick={() => {
              if (step === "wb-done") return onClose();
              if (step === "wb-review") return setStep(1);
              if (step > 1) return setStep(step - 1);
              onClose();
            }}
            className="text-sm font-semibold text-gray-600"
          >
            {step === "wb-done" ? "Close" : step === 1 ? "Cancel" : "Back"}
          </button>

          {step === 1 && (
            <button onClick={onNextFromFile} disabled={!file || busy} className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50">
              {busy ? "Reading…" : isWorkbook ? "Auto-clean & review" : "Next"}
            </button>
          )}
          {step === "wb-review" && (
            <button
              onClick={doWorkbookCommit}
              disabled={busy || !totalToImport || wbBlocked || wbViews.some((v) => (wbListKinds[v] || []).length && !wbKinds[v])}
              title={wbBlocked ? "Fix the problems in red first" : ""}
              className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
            >
              {busy ? "Importing…" : `Import ${totalToImport.toLocaleString()} records`}
            </button>
          )}
          {step === "wb-done" && (
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold">
              Done
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              disabled={checking || !!quality?.blocking}
              title={quality?.blocking ? "Fix the problems in red first" : ""}
              className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
            >
              Next
            </button>
          )}
          {/* No destination = rows that are saved but show under no tab at all.
              413 of Paul Jimoulis's 414 rows went in that way on 1 Sep 2026
              (CRM-2026-00017), so the button is held until he has picked one. */}
          {step === 3 && (
            <button
              onClick={doCsvCommit}
              disabled={busy || !hasDestination || (preview?.list_kinds?.length > 0 && !listKind)}
              title={!listKind ? "Say what this list is first" : hasDestination ? "" : "Choose a list or category for these rows first"}
              className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
            >
              {busy ? "Importing…" : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
